import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { StepProxyService } from '../../../proxies/item/step.proxy.service';
import { Step, StepSearch, StrictStep } from '../../../models/engineering/step';
import { TranslateService } from '@ngx-translate/core';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues } from '@card-software/library';
import { ProductDevelopmentAdminGroup, ProductDevelopmentViewerGroup } from '../../../models/authorization/enum/user-group.enum';
import { AuthService } from '@card-software/nexus-utilities';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-steps',
  templateUrl: './steps.component.html',
  styleUrl: './steps.component.scss',
})
export class StepsComponent {
  private stepProxyService = inject(StepProxyService);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  isLoading = signal<number>(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'steps-filters', config: this.filterConfig });
  showFilters = signal<boolean>(true);
  steps: Step[] = [];

  StrictStep = StrictStep;

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.initializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));
  }

  canEdit() {
    // if Admin
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.ProcessStepsAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.ProcessStepsViewer)) {
      return false;
    }
    // Default
    return false;
  }

  private updateLoadingState(isLoading: boolean): void {
    this.isLoading.update((value) => (isLoading ? value + 1 : value - 1));
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    this.filterConfig.set({
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'number',
          formControlName: 'stepIdParam',
          label: translate('Step_ID', 'Step ID'),
          placeholder: translate('Step_Enter_Id', 'Enter step id'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'stepName',
          label: translate('Step_Name', 'Name'),
          placeholder: translate('Step_Enter_Name', 'Enter step name'),
          defaultVisible: true,
        },
      ],
    });
  }

  onSearch(criteria: StepSearch) {
    this.updateLoadingState(true);
    this.stepProxyService
      .searchSteps(criteria)
      .pipe(finalize(() => this.updateLoadingState(false)))
      .subscribe({
        next: (steps) => {
          this.steps = steps.data || [];
        },
      });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.onSearch({
      stepIdParam: (filters['stepIdParam'] as number | null) ?? undefined,
      stepName: typeof filters['stepName'] === 'string' ? filters['stepName'].trim() || undefined : undefined,
    });
  }

  redirectToDetails(id?: string | number): void {
    const queryParams = id ? { id: id } : { id: 'new' };
    this.router.navigate(['/step-details'], { queryParams });
  }
}
